import React from 'react';
import { Stack, Box, Typography, Avatar } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ChatIcon from '@mui/icons-material/Chat';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { useTranslation } from 'react-i18next';
import { colorTokens } from '@/widgets/frontdesk/design-system';
import {
  quoteDecisionText,
  readQuoteDecision,
} from '@/widgets/frontdesk/providers/chatwoot/utils/quote-decision-activity';
import { useGomakeTheme } from '@/hooks/use-gomake-thme';
import { FONT_FAMILY } from '@/utils/font-family';
import { MessageDateTimeComponent } from '@/widgets/messenger/components/message-date-time-component';
import { GmailIcon } from '@/icons/gmail-icon';
import { OutlookIcon } from '@/icons/outlook-icon';
import {
  BackendNotification,
  extractChannel,
  extractEmailProvider,
  isNotificationUnread,
} from '@/services/frontdesk/utils/notification-type-mapper';

interface MessageNotificationItemProps {
  notification: BackendNotification;
  onClick: (notification: BackendNotification) => void;
}

const MessageNotificationItem: React.FC<MessageNotificationItemProps> = ({
  notification,
  onClick,
}) => {
  const { t } = useTranslation();
  const dir: 'rtl' | 'ltr' = t('direction');
  const { primaryColor, neutralColor } = useGomakeTheme();

  const isUnread = isNotificationUnread(notification);

  const channelRaw = extractChannel(notification) || 'DM';
  const emailProvider =
    channelRaw === 'EMAIL' ? extractEmailProvider(notification) : null;

  const channel =
    emailProvider === 'gmail'
      ? 'Gmail'
      : emailProvider === 'outlook'
        ? 'Outlook'
        : channelRaw === 'WHATSAPP'
          ? 'WhatsApp'
          : channelRaw.charAt(0) + channelRaw.slice(1).toLowerCase();

  const titleAsSender =
    notification.title &&
    notification.title !== 'New message' &&
    notification.title !== 'New conversation'
      ? notification.title
      : null;
  const senderName =
    notification.metadata?.senderName ||
    notification.metadata?.userName ||
    ((notification.type === 'DM_RECEIVED' ||
      notification.type === 'DM_MENTION') &&
      titleAsSender) ||
    (channelRaw === 'WHATSAPP' &&
      notification.title?.replace(/^New WhatsApp from\s+/i, '').trim()) ||
    'Unknown User';
  const senderAvatar =
    notification.metadata?.senderAvatar || notification.metadata?.userAvatar;

  const quoteDecision = readQuoteDecision(notification.metadata);
  const quotePalette =
    quoteDecision?.decision === 'approved' ? colorTokens.success : colorTokens.error;
  const QuoteDecisionIcon =
    quoteDecision?.decision === 'approved' ? CheckCircleRoundedIcon : CancelRoundedIcon;
  const bodyText = quoteDecision
    ? quoteDecisionText(quoteDecision, t)
    : notification.body || notification.title;

  const getChannelLabel = () => {
    if (!quoteDecision) return channel;
    return t(
      quoteDecision.decision === 'approved'
        ? 'documentStatus.ApprovedByClient'
        : 'documentStatus.RejectedByClient'
    ).trim();
  };

  const getChannelIcon = () => {
    if (quoteDecision) {
      return <QuoteDecisionIcon sx={{ fontSize: 12, color: quotePalette[600] }} />;
    }
    if (emailProvider === 'gmail') {
      return <GmailIcon size={12} />;
    }
    if (emailProvider === 'outlook') {
      return <OutlookIcon size={12} />;
    }
    switch (channelRaw) {
      case 'EMAIL':
        return <EmailOutlinedIcon sx={{ fontSize: 12, color: '#2e3092' }} />;
      case 'WHATSAPP':
        return <WhatsAppIcon sx={{ fontSize: 12, color: '#25D366' }} />;
      case 'DM':
      default:
        return <ChatIcon sx={{ fontSize: 12, color: '#2e3092' }} />;
    }
  };

  const getChannelColor = () => {
    if (quoteDecision) return quotePalette[600];
    if (emailProvider === 'gmail') return '#EA4335';
    if (emailProvider === 'outlook') return '#0078D4';
    switch (channelRaw) {
      case 'EMAIL':
        return '#2e3092';
      case 'WHATSAPP':
        return '#25D366';
      case 'DM':
      default:
        return '#2e3092';
    }
  };

  return (
    <Stack
      direction="row"
      gap="12px"
      padding="12px 20px"
      dir={dir}
      sx={{
        cursor: 'pointer',
        backgroundColor: isUnread ? 'transparent' : neutralColor(50),
        opacity: isUnread ? 1 : 0.7,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
        direction: dir,
        textAlign: 'start',
        '&:hover': {
          backgroundColor: isUnread ? neutralColor(50) : neutralColor(100),
          opacity: 1,
        },
        borderBottom: `1px solid ${neutralColor(100)}`,
      }}
      onClick={() => onClick(notification)}
    >
      <Avatar
        src={senderAvatar}
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          backgroundColor: quoteDecision ? quotePalette[100] : primaryColor(100),
          color: quoteDecision ? quotePalette[600] : primaryColor(500),
          ...FONT_FAMILY.Lexend(600, 14),
        }}
      >
        {quoteDecision ? (
          <QuoteDecisionIcon sx={{ fontSize: 24 }} />
        ) : (
          senderName.charAt(0).toUpperCase()
        )}
      </Avatar>

      <Stack flex={1} gap="4px" sx={{ minWidth: 0, overflow: 'hidden', direction: dir }}>
        <Stack
          direction="row"
          alignItems="center"
          gap="8px"
          sx={{ minWidth: 0, overflow: 'hidden', direction: dir }}
        >
          <Typography
            sx={{
              ...FONT_FAMILY.Lexend(isUnread ? 600 : 500, 14),
              color: primaryColor(900),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
              textAlign: 'start',
            }}
          >
            {senderName}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 6px',
              borderRadius: '4px',
              backgroundColor: `${getChannelColor()}10`,
              flexShrink: 0,
            }}
          >
            {getChannelIcon()}
            <Typography
              sx={{
                ...FONT_FAMILY.Lexend(500, 11),
                color: getChannelColor(),
              }}
            >
              {getChannelLabel()}
            </Typography>
          </Box>
        </Stack>

        <Typography
          sx={{
            ...FONT_FAMILY.Lexend(400, 13),
            color: neutralColor(600),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            textAlign: 'start',
          }}
        >
          {bodyText}
        </Typography>

        <Box sx={{ color: neutralColor(400), minWidth: 0, textAlign: 'start' }}>
          <MessageDateTimeComponent date={new Date(notification.createdAt)} />
        </Box>
      </Stack>

      {isUnread && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: primaryColor(500),
            flexShrink: 0,
            marginTop: '16px',
          }}
        />
      )}
    </Stack>
  );
};

export { MessageNotificationItem };
